import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Currency,
  DateTime,
  DeleteConfirmation,
  HelpTooltip,
  Icon,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
  Tooltip,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useRowSelection } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import TitleTooltip from '../../TitleTooltip';
import AddMultipleDialog from './AddMultipleDialog';
import BulkDelete from './BulkDelete';
import GenerateRevRecEntries from './GenerateRevRecEntries';
import RevenueRecognitionEntryForm from './RevenueRecognitionEntryForm';
import SettingsActionsBanner from '../../SettingsActionsBanner';

export default function RevenueRecognitionLedger({ project }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);

  const fetchData = useCallback(async () => {
    const [{ data: entries }, { data: revenueSummary }] = await Promise.all([
      api.www.workspaces(workspace.id).projectSettings(project.id).revenueRecognitionEntries().get(),
      api.www.workspaces(workspace.id).projectSettings(project.id).revenueRecognitionSummary(),
    ]);
    setData(entries);
    setSummary(revenueSummary);
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selection = useRowSelection({ rows: data });

  const [dialog, setDialog] = useState(null);
  const toast = useToast();

  const handleSaved = async () => {
    await fetchData();
    setDialog(null);
  };

  const handleShowForm = (revenueRecognitionEntry = null) => {
    setDialog(
      <RevenueRecognitionEntryForm
        currency={project.currency}
        projectId={project.id}
        revenueRecognitionEntryId={revenueRecognitionEntry?.id}
        onSaved={handleSaved}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleShowMultipleForm = () => {
    setDialog(
      <AddMultipleDialog
        currency={project.currency}
        project={project}
        onSaved={handleSaved}
        onCancel={() => setDialog(null)}
      />,
    );
  };

  const confirmation = useConfirmation();
  const handleDelete = async (entry) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Delete Revenue Recognition Entry"
        resolve={async (result) => {
          if (result) {
            await api.www
              .workspaces(workspace.id)
              .projectSettings(project.id)
              .revenueRecognitionEntries(entry.id)
              .delete();
            toast.success('Revenue Recognition Entry deleted successfully.');
            await fetchData();
            selection.deselect(entry.id);
          }
          resolve(result);
        }}>
        Are you sure you want to delete this revenue recognition entry?
      </DeleteConfirmation>
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        ids={selection.ids}
        currency={project.currency}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  const handleGenerate = () => {
    setDialog(<GenerateRevRecEntries projectId={project.id} onClose={() => setDialog(null)} onRefetch={fetchData} />);
  };

  // Generation needs a services-revenue budget always, plus a billable-hours budget for the methods that
  // recognize by hours. The services-cost method recognizes off cost, so it has no hours requirement.
  const generateStatus = useMemo(() => {
    const errors = [];
    if (summary) {
      if (!summary.useBudget) errors.push('Project must use budget.');
      if (!summary.servicesRevenue) errors.push('Project must have a services revenue budget.');
      if (
        ['manual', 'automated_hours', 'automated_tm_basis'].includes(project.revenueRecognitionMethod) &&
        !summary.billableHours
      ) {
        errors.push('Project must have a billable hours budget.');
      }
    }
    return { disabled: errors.length > 0, errors };
  }, [summary, project.revenueRecognitionMethod]);

  if (!data || !summary) return <PageLoader />;

  const total = {
    recognized: _.round(_.sumBy(data, 'amount'), 2),
    scheduled: summary.scheduled,
  };

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Revenue Recognition Ledger</Page.Title>
        <TitleTooltip message="Schedule the recognition of the fixed fee project revenue." />

        {project.permissions.edit && (
          <Page.Actions>
            <IconButton
              icon="gears"
              tooltip={generateStatus.disabled ? generateStatus.errors.join(' ') : 'Generate'}
              disabled={generateStatus.disabled}
              onClick={handleGenerate}
            />

            <IconButton icon="clone-plus" tooltip="Add Multiple" onClick={handleShowMultipleForm} />

            <IconButton icon="plus" tooltip="Add" onClick={() => handleShowForm(null)} />
          </Page.Actions>
        )}
      </Page.TabHeader>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListViewSelection.Column
              testId="revenue_recognition_entries_bulk_checkbox"
              selection={selection}
              isVisible={project.permissions.edit}
              disabled={!data.length}
            />
            <ListView.Column width="8rem">Date</ListView.Column>
            <ListView.Column minWidth="20rem">Notes</ListView.Column>
            <ListView.Column width="10rem" align="right">
              Amount
            </ListView.Column>
            <ListViewActions.Column isVisible={project.permissions.edit} />
          </ListView.Header>

          <ListView.Body>
            {data.map((entry) => {
              return (
                <ListView.Row
                  key={entry.id}
                  onClick={project.permissions.edit ? () => handleShowForm(entry) : undefined}>
                  <ListViewSelection.Cell
                    id={entry.id}
                    selection={selection}
                    testId="checkbox_revenue_recognition_entry"
                  />
                  <ListView.Cell>
                    <DateTime value={entry.date} />
                  </ListView.Cell>
                  <ListView.Cell>{entry.notes}</ListView.Cell>
                  <ListView.Cell>
                    <Currency value={entry.amount} currency={entry.project.currency} />
                  </ListView.Cell>
                  <ListViewActions isVisible={project.permissions.edit}>
                    <ListViewActions.Edit onClick={() => handleShowForm(entry)} />
                    <hr />
                    <ListViewMenu>
                      <ListViewMenu.Item onClick={() => handleShowForm(entry)}>Edit</ListViewMenu.Item>
                      <ListViewMenu.DeleteItem onClick={() => handleDelete(entry)}>Delete</ListViewMenu.DeleteItem>
                    </ListViewMenu>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length > 0 && (
              <ListView.TotalRow>
                <ListView.Cell />
                <ListView.Cell>Total</ListView.Cell>
                <ListView.Cell />
                <ListView.Cell>
                  {total.recognized > total.scheduled && (
                    <Tooltip
                      style={{ marginRight: '0.5rem' }}
                      message="The total of the Revenue Recognition Ledger exceeds the total of the Fixed Fee Billing Schedule.">
                      <Icon icon="exclamation-triangle" color={colors.warning} />
                    </Tooltip>
                  )}
                  <Currency value={total.recognized} currency={project.currency} />
                </ListView.Cell>
                <ListViewActions.Cell isVisible={project.permissions.edit} />
              </ListView.TotalRow>
            )}

            {summary.useBudget && (
              <ListView.TotalRow>
                <ListView.Cell />
                {/* Labels start in the Date column like every other ledger's total row. This one is wider than
                  that column, so it runs on into the empty Notes cell beside it rather than wrapping. */}
                <ListView.Cell style={{ whiteSpace: 'nowrap' }}>
                  Budget Recognizable Revenue
                  <HelpTooltip
                    style={{ marginLeft: '0.5rem' }}
                    message="The Budget Recognizable Revenue is the services revenue budget plus the non-billable expenses budget. It does not include billable expenses, products, additional services, or other items."
                  />
                </ListView.Cell>
                <ListView.Cell />
                <ListView.Cell>
                  <Currency value={summary.budgetRecognizableRevenue} currency={project.currency} />
                </ListView.Cell>
                <ListViewActions.Cell isVisible={project.permissions.edit} />
              </ListView.TotalRow>
            )}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Revenue Recognition Entry" />
        </ListView>
      </Page.ListView>

      {dialog}

      {selection.any && (
        <SettingsActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <BulkActionMenu.Item
                onClick={() => {
                  setIsOpen(false);
                  handleBulkDelete();
                }}>
                Delete
              </BulkActionMenu.Item>
            )}
          </BulkActionMenu>
        </SettingsActionsBanner>
      )}
    </>
  );
}
